import { Search, X } from 'lucide-react';
import * as React from 'react';

import { Input } from '@apps/web/components/ui/input';
import { cn } from '@apps/web/lib/utils';

type SearchInputProps = Omit<React.ComponentProps<'input'>, 'onChange'> & {
    readonly value: string;
    readonly onValueChange: (value: string) => void;
    readonly containerClassName?: string;
};

// Search input with a leading search icon and a trailing clear (X) button.
// The clear button only renders when there is text to clear, is hidden while
// the input is disabled, and preserves input focus on click so the user can
// keep typing afterward.
const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
    (
        {
            value,
            onValueChange,
            className,
            containerClassName,
            placeholder,
            disabled,
            ...props
        },
        ref,
    ) => {
        const hasValue = value.length > 0;
        const showClear = hasValue && !disabled;
        return (
            <div className={cn('relative', containerClassName)}>
                <Search className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                <Input
                    ref={ref}
                    value={value}
                    onChange={(event) => onValueChange(event.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={cn('pl-9', showClear && 'pr-9', className)}
                    {...props}
                />
                {showClear && (
                    <button
                        type="button"
                        aria-label="Clear search"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => onValueChange('')}
                        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-1/2 right-2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm outline-none focus-visible:ring-2"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>
        );
    },
);

SearchInput.displayName = 'SearchInput';

export { SearchInput };
